import { alpha, Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { ReactNode } from 'react';

interface SwitchCardProps {
  label: ReactNode;
  /** Optional helper line under the label, for toggles whose effect is not obvious. */
  description?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

/**
 * Outlined row with a label (plus optional description) and a switch on the right.
 * The whole row is a label element, so clicking anywhere toggles the switch, and the
 * border/background turn primary while it is on to make the active state readable at a glance.
 * Controlled component; see SwitchCardField for the react-hook-form bound version.
 */
export function SwitchCard({
  label,
  description,
  checked,
  onChange,
  disabled = false,
}: SwitchCardProps) {
  return (
    <FormControlLabel
      labelPlacement="start"
      disabled={disabled}
      disableTypography
      control={<Switch checked={checked} onChange={(event) => onChange(event.target.checked)} />}
      label={
        <Box component="span" sx={{ display: 'block', flex: 1, minWidth: 0 }}>
          <Typography
            component="span"
            variant="body2"
            display="block"
            color={disabled ? 'text.disabled' : 'text.primary'}
          >
            {label}
          </Typography>
          {description && (
            <Typography
              component="span"
              variant="caption"
              display="block"
              color={disabled ? 'text.disabled' : 'text.secondary'}
            >
              {description}
            </Typography>
          )}
        </Box>
      }
      sx={(theme) => ({
        margin: 0,
        width: '100%',
        gap: 1.5,
        paddingX: 1.5,
        paddingY: 1,
        border: 1,
        borderRadius: '0.375rem', // Matches the outlined inputs
        borderColor: checked ? 'primary.main' : 'divider',
        backgroundColor: checked ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
        transition: theme.transitions.create(['border-color', 'background-color']),
        '&:hover': {
          borderColor: checked ? 'primary.main' : 'text.disabled',
        },
      })}
    />
  );
}
